import { Check } from "lucide-react";
import { Link } from "react-router";

import { timeAgo, useMarkAllNotificationsRead, useMarkNotificationRead, useNotificationFeed, type Notification } from "nazare-ui/components";
import { PageSection, PageSectionBadge } from "nazare-ui/components/page-section";
import { cn } from "nazare-ui/utils";

import { SkeletonBlock } from "../skeleton-block";
import { useDocumentTitle } from "../use-document-title";
import styles from "./notifications-all.module.css";

/** Shimmering placeholder shaped like a real `.item` row (dot + title +
 * description + timestamp), not a bare rectangle — matches the skeleton
 * convention used everywhere else in this app (e.g. ../skeleton-row.tsx). */
function NotificationItemSkeleton({ index }: { index: number }) {
  return (
    <div className={styles.item} aria-hidden="true">
      <SkeletonBlock width={7} height={7} radius="var(--radius-full)" />
      <span className={styles.itemBody}>
        <SkeletonBlock width={`${45 + ((index * 13) % 30)}%`} height={13} />
        <SkeletonBlock width={`${65 + ((index * 11) % 20)}%`} height={12} />
        <SkeletonBlock width={60} height={11} />
      </span>
    </div>
  );
}

/**
 * `/notifications/all` — the personal full-inbox page the bell dropdown's
 * "View all notifications" link points at (`nazare-ui/components`'s
 * `<NotificationBell>`, see that component's own doc comment). Mirrors
 * nazare-frontend's own `/notifications` page (`app/web/app/screens/
 * notifications/notifications-page.tsx`): a flat, ungrouped, unpaginated
 * list of whatever `useNotificationFeed()` already has (same react-query
 * cache the dropdown uses, so opening this page never re-fetches on top of
 * an already-warm dropdown) — no "load more", matching the core app, which
 * also just fetches a fixed page and stops.
 *
 * No on-page `<h1>` "Notifications" — the breadcrumb trail already shows
 * that (see root-layout.tsx), same convention as every other page in this
 * app. The unread count + "Mark all read" + per-item content below IS real
 * functional content, not a redundant heading, so it stays.
 */
export function NotificationsAllPage() {
  useDocumentTitle("Notifications");

  const { notifications, hasUnread, isLoading, isError, isSessionExpired } =
    useNotificationFeed();
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllNotificationsRead();

  const markItemRead = (notification: Notification) => {
    if (notification.read_at == null) {
      markRead(notification.id);
    }
  };
  const visibleUnreadCount = notifications.filter((notification) => notification.read_at == null).length;

  return (
    <div className={styles.page}>
      <PageSection
        className={styles.section}
        title="Notifications"
        description="Loaded notifications"
        count={isLoading && notifications.length === 0 ? undefined : notifications.length}
        badges={visibleUnreadCount > 0 && <PageSectionBadge>{visibleUnreadCount} unread</PageSectionBadge>}
        actions={hasUnread && (
          <button type="button" className={styles.markAllRead} onClick={() => markAllRead()}>
            <Check size={13} aria-hidden="true" />
            Mark all read
          </button>
        )}
      >
        <div className={styles.list}>
          {isLoading && notifications.length === 0 ? (
            <div aria-hidden="true">
              {Array.from({ length: 6 }, (_, index) => (
                <NotificationItemSkeleton key={index} index={index} />
              ))}
            </div>
          ) : isSessionExpired && notifications.length === 0 ? (
            <p className={styles.empty}>Sign in again to see notifications.</p>
          ) : isError && notifications.length === 0 ? (
            <p className={styles.empty}>Couldn't load notifications.</p>
          ) : notifications.length === 0 ? (
            <p className={styles.empty}>No notifications yet.</p>
          ) : (
            notifications.map((notification) => {
              const isUnread = notification.read_at == null;
              const itemProps = {
                className: cn(styles.item, isUnread && styles.itemUnread),
                onClick: () => markItemRead(notification),
              };
              const content = (
                <>
                  <span className={cn(styles.dot, isUnread && styles.dotUnread)} aria-hidden="true" />
                  <span className={styles.itemBody}>
                    <span className={cn(styles.itemTitle, isUnread && styles.itemTitleUnread)}>
                      {notification.title}
                    </span>
                    {notification.body && <span className={styles.itemDesc}>{notification.body}</span>}
                    <span className={styles.itemTime}>{timeAgo(notification.created_at)}</span>
                  </span>
                </>
              );
              if (notification.url) {
                return notification.url.startsWith("/") && !notification.url.startsWith("//") ? (
                  <Link key={notification.id} to={notification.url} {...itemProps}>{content}</Link>
                ) : (
                  <a key={notification.id} href={notification.url} target="_blank" rel="noopener noreferrer" {...itemProps}>
                    {content}
                  </a>
                );
              }
              return <button key={notification.id} type="button" {...itemProps}>{content}</button>;
            })
          )}
        </div>
      </PageSection>
    </div>
  );
}
